import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent
} from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";

import { useGetAutostoreGridsQuery } from "~/redux/warehouse/autostoreGrid.hooks";

type Props = {
  selectedGrid: string | undefined;
  onSelect: (value: string | undefined) => void;
  /** When the parent provides the field label (e.g. OverviewFieldRow), omit the floating label. */
  hideLabel?: boolean;
  variant?: "standard" | "outlined";
  selectSx?: SxProps<Theme>;
  /** Used with `hideLabel` + `outlined`: id of the external label element for a11y. */
  externalLabelId?: string;
};

export function AutostoreGridSelector({
  selectedGrid,
  onSelect,
  hideLabel = false,
  variant = "standard",
  selectSx,
  externalLabelId
}: Props) {
  const { t } = useTranslation();
  const { data: autostoreGrids } = useGetAutostoreGridsQuery();

  const handleChange = (event: SelectChangeEvent) => {
    onSelect(event.target.value);
  };

  if (variant === "outlined") {
    return (
      <FormControl fullWidth size="small">
        {!hideLabel && (
          <InputLabel id="autostore-grid-selector-label">
            {t("autostore grid")}
          </InputLabel>
        )}
        <Select
          aria-label="autostore grid selector"
          fullWidth
          labelId={
            hideLabel ? externalLabelId : "autostore-grid-selector-label"
          }
          {...(!hideLabel ? { label: t("autostore grid") } : {})}
          value={autostoreGrids && selectedGrid ? selectedGrid : ""}
          onChange={handleChange}
          variant="outlined"
          sx={selectSx}
          {...(hideLabel && externalLabelId
            ? {
                displayEmpty: true,
                slotProps: {
                  input: {
                    "aria-labelledby": externalLabelId
                  }
                }
              }
            : {})}
        >
          {autostoreGrids?.map((grid) => (
            <MenuItem key={grid.autostoreGridId} value={grid.autostoreGridId}>
              {grid.autostoreGridName}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  return (
    <>
      {!hideLabel && <InputLabel shrink>{t("autostore grid")}</InputLabel>}
      <Select
        aria-label="autostore grid selector"
        fullWidth
        value={autostoreGrids && selectedGrid ? selectedGrid : ""}
        onChange={handleChange}
        variant="standard"
      >
        {autostoreGrids?.map((grid) => (
          <MenuItem key={grid.autostoreGridId} value={grid.autostoreGridId}>
            {grid.autostoreGridName}
          </MenuItem>
        ))}
      </Select>
    </>
  );
}
